import { createAuthClient } from "better-auth/react";
import { adminClient, twoFactorClient } from "better-auth/client/plugins";
import {
  accessContextResponseSchema,
  apiErrorSchema,
  type AccessContextResponse,
  type ApiErrorCode,
} from "@kindtutor/api-contract";

export type ApiFetch = typeof globalThis.fetch;

function isPrivateDevelopmentHostname(hostname: string) {
  const normalized = hostname.toLowerCase();
  if (normalized === "localhost" || normalized === "127.0.0.1") return true;

  const octets = normalized.split(".").map(Number);
  if (
    octets.length !== 4 ||
    octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)
  ) {
    return false;
  }

  const first = octets[0]!;
  const second = octets[1]!;
  return (
    first === 10 ||
    first === 127 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168)
  );
}

export function resolveDevelopmentApiBaseUrl(
  configuredBaseUrl: string,
  pageHostname: string,
  development: boolean,
) {
  if (!development || !isPrivateDevelopmentHostname(pageHostname)) {
    return configuredBaseUrl;
  }

  try {
    const configured = new URL(configuredBaseUrl);
    if (
      configured.protocol !== "http:" ||
      !["localhost", "127.0.0.1"].includes(configured.hostname.toLowerCase())
    ) {
      return configuredBaseUrl;
    }
    configured.hostname = pageHostname;
    return configured.toString().replace(/\/$/, "");
  } catch {
    return configuredBaseUrl;
  }
}

function headerRecord(source?: HeadersInit) {
  if (!source) return {};
  if (source instanceof Headers) return Object.fromEntries(source.entries());
  if (Array.isArray(source)) return Object.fromEntries(source);
  return { ...source };
}

function headerValue(headers: Record<string, string>, name: string) {
  const key = Object.keys(headers).find(
    (candidate) => candidate.toLowerCase() === name.toLowerCase(),
  );
  return key ? headers[key] : undefined;
}

function createRequestId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `web-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

export function createApiFetch(
  clientVersion: string,
  fetchImplementation?: ApiFetch,
): ApiFetch {
  return async (input, init) => {
    const headers = {
      ...headerRecord(input instanceof Request ? input.headers : undefined),
      ...headerRecord(init?.headers),
    };
    const requestId = headerValue(headers, "X-Request-Id") ?? createRequestId();
    if (!headerValue(headers, "X-Request-Id")) {
      headers["X-Request-Id"] = requestId;
    }
    if (!headerValue(headers, "X-Correlation-Id")) {
      headers["X-Correlation-Id"] = requestId;
    }
    if (!headerValue(headers, "X-Client-Version")) {
      headers["X-Client-Version"] = clientVersion;
    }
    return (fetchImplementation ?? globalThis.fetch)(input, {
      ...init,
      headers,
    });
  };
}

export class KindTutorApiError extends Error {
  constructor(
    message: string,
    readonly code: ApiErrorCode | null,
    readonly status: number,
    readonly requestId: string | null,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "KindTutorApiError";
  }
}

const validationMessageTranslations: Record<string, string> = {
  "At least one student interaction method must be enabled":
    "Włącz co najmniej jeden sposób interakcji ucznia.",
  "Percentage must be positive": "Procent wdrożenia musi być większy od zera.",
  "Warranty cannot expire before purchase":
    "Gwarancja nie może kończyć się przed datą zakupu.",
  "Replacement due date requires a replacement order.":
    "Termin wymiany wymaga zlecenia wymiany.",
  "Resolution is required for resolved service orders.":
    "Uzupełnij sposób rozwiązania zamkniętego zlecenia serwisowego.",
  "Activity mode does not belong to activity type":
    "Wybrany tryb nie pasuje do rodzaju aktywności.",
  "Conversation context is too long": "Kontekst rozmowy jest zbyt długi.",
  "Quiz options must be unique": "Odpowiedzi w quizie nie mogą się powtarzać.",
  "Realtime usage cannot exceed lesson duration":
    "Czas rozmowy nie może przekraczać długości lekcji.",
  "Duty end must be later than duty start":
    "Koniec dyżuru musi przypadać po jego rozpoczęciu.",
  "Resolution target cannot be shorter than response target":
    "Termin rozwiązania nie może być krótszy niż termin odpowiedzi.",
  "Support end must be later than support start":
    "Godzina zakończenia wsparcia musi przypadać po godzinie rozpoczęcia.",
  "Contract end date cannot precede signing date":
    "Data końca umowy nie może poprzedzać daty podpisania.",
  "Lost reason is required": "Podaj powód utraty szansy sprzedaży.",
  "Exactly one pilot target is required":
    "Wybierz dokładnie jedną szkołę lub szansę sprzedaży dla pilotażu.",
  "Pilot end date must not precede its start date":
    "Data zakończenia pilotażu nie może poprzedzać daty rozpoczęcia.",
  "A follow-up action is required for this decision":
    "Podaj następne działanie dla wybranej decyzji.",
  "A follow-up action is required for a failed or blocked drill":
    "Podaj działanie naprawcze dla niezaliczonej lub zablokowanej próby.",
  "Follow-up actions are required for this decision":
    "Podaj dalsze działania dla wybranej decyzji.",
  "Invoice due date must not precede issue date":
    "Termin płatności nie może poprzedzać daty wystawienia faktury.",
};

type ValidationIssue = {
  message?: unknown;
};

export function localizeApiServerMessage(message: string) {
  const directTranslation = validationMessageTranslations[message];
  if (directTranslation) return directTranslation;

  try {
    const parsed: unknown = JSON.parse(message);
    const issues = Array.isArray(parsed) ? parsed : [parsed];
    const translated = issues
      .map((issue) => {
        if (!issue || typeof issue !== "object") return null;
        const issueMessage = (issue as ValidationIssue).message;
        if (typeof issueMessage !== "string") return null;
        return validationMessageTranslations[issueMessage] ?? null;
      })
      .filter((value): value is string => Boolean(value));
    if (translated.length) return [...new Set(translated)].join(" ");
    if (issues.length) {
      return "Sprawdź poprawność danych w formularzu i spróbuj ponownie.";
    }
  } catch {
    // A normal, already user-facing server message does not need JSON parsing.
  }

  return message;
}

export const localizedApiErrorSchema = apiErrorSchema.transform((value) => ({
  ...value,
  error: {
    ...value.error,
    message: localizeApiServerMessage(value.error.message),
  },
}));

export function parseApiInput<T>(
  schema: { parse(input: unknown): T },
  input: unknown,
): T {
  try {
    return schema.parse(input);
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(localizeApiServerMessage(error.message), {
        cause: error,
      });
    }
    throw new Error(
      "Sprawdź poprawność danych w formularzu i spróbuj ponownie.",
      { cause: error },
    );
  }
}

export function getApiErrorMessage(
  code: ApiErrorCode | null,
  fallback: string,
  serverMessage?: string,
) {
  const localizedServerMessage = serverMessage
    ? localizeApiServerMessage(serverMessage)
    : undefined;
  switch (code) {
    case "UNAUTHENTICATED":
      return "Sesja wygasła. Zaloguj się ponownie.";
    case "FORBIDDEN":
      return "Nie masz uprawnień do wykonania tej operacji.";
    case "RATE_LIMITED":
      return "Wykonano zbyt wiele prób. Poczekaj chwilę i spróbuj ponownie.";
    case "QUOTA_EXCEEDED":
      return "Limit wykorzystania został wyczerpany. Skontaktuj się z administratorem.";
    case "DEPENDENCY_UNAVAILABLE":
    case "SERVICE_UNAVAILABLE":
      return "Usługa jest chwilowo niedostępna. Spróbuj ponownie za chwilę.";
    case "INTERNAL_ERROR":
      return "Coś poszło nie tak po naszej stronie. Spróbuj ponownie.";
    case "BAD_REQUEST":
    case "NOT_FOUND":
    case "CONFLICT":
    case "UNSUPPORTED_MEDIA_TYPE":
    case "PAYLOAD_TOO_LARGE":
      return localizedServerMessage ?? fallback;
    default:
      return localizedServerMessage ?? fallback;
  }
}

export async function throwApiError(
  response: Response,
  fallback: string,
): Promise<never> {
  let body: unknown;
  try {
    body = await response.clone().json();
  } catch {
    body = null;
  }
  const parsed = apiErrorSchema.safeParse(body);
  const code = parsed.success ? parsed.data.error.code : null;
  const requestId = parsed.success
    ? parsed.data.error.requestId
    : response.headers.get("X-Request-Id");
  throw new KindTutorApiError(
    getApiErrorMessage(
      code,
      fallback,
      parsed.success ? parsed.data.error.message : undefined,
    ),
    code,
    response.status,
    requestId,
    parsed.success ? parsed.data.error.details : undefined,
  );
}

export type ApiResponseSchema<T> = {
  parse(input: unknown): T;
};

export async function fetchApiJson<T>(
  fetcher: ApiFetch,
  input: RequestInfo | URL,
  init: RequestInit,
  schema: ApiResponseSchema<T>,
  fallback: string,
): Promise<T> {
  const response = await fetcher(input, init);
  if (!response.ok) await throwApiError(response, fallback);
  return schema.parse(await response.json());
}

export async function fetchApiBlob(
  fetcher: ApiFetch,
  input: RequestInfo | URL,
  init: RequestInit,
  fallback: string,
  fallbackFilename = "download",
) {
  const response = await fetcher(input, init);
  if (!response.ok) await throwApiError(response, fallback);
  const disposition = response.headers.get("Content-Disposition");
  const encodedFilename = disposition?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  const quotedFilename = disposition?.match(/filename="([^"]+)"/)?.[1];
  return {
    blob: await response.blob(),
    filename: encodedFilename
      ? decodeURIComponent(encodedFilename)
      : (quotedFilename ?? fallbackFilename),
  };
}

export function createKindTutorAuthClient(
  baseURL: string,
  clientVersion = "kindtutor-web/0.1.0",
  fetchImplementation?: ApiFetch,
) {
  const apiFetch = createApiFetch(clientVersion, fetchImplementation);
  return createAuthClient({
    baseURL,
    basePath: "/v1/auth",
    fetchOptions: { customFetchImpl: apiFetch },
    plugins: [adminClient(), twoFactorClient({ twoFactorPage: "/two-factor" })],
  });
}

export async function fetchAccessContext(
  baseURL: string,
  fetchImplementation: ApiFetch = createApiFetch("kindtutor-web/0.1.0"),
): Promise<AccessContextResponse | null> {
  const response = await fetchImplementation(`${baseURL}/v1/me`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) return null;
  if (!response.ok) {
    await throwApiError(response, "Nie udało się wczytać dostępu do konta.");
  }
  return accessContextResponseSchema.parse(await response.json());
}

export type AuthErrorLike = {
  code?: string | undefined;
  message?: string | undefined;
  status?: number | undefined;
};

export function getAuthErrorMessage(error: AuthErrorLike | null | undefined) {
  switch (error?.code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return "Nieprawidłowy adres e-mail lub hasło.";
    case "EMAIL_NOT_VERIFIED":
      return "Najpierw potwierdź adres e-mail. Wysłaliśmy nową wiadomość z linkiem.";
    case "USER_BANNED":
      return "To konto zostało zablokowane. Skontaktuj się z administratorem.";
    case "INVALID_TOKEN":
    case "TOKEN_EXPIRED":
      return "Ten link jest nieprawidłowy lub wygasł. Poproś o nowy link.";
    case "PASSWORD_TOO_SHORT":
      return "Hasło musi mieć co najmniej 12 znaków.";
    case "PASSWORD_TOO_LONG":
      return "Hasło może mieć maksymalnie 128 znaków.";
    case "TOO_MANY_REQUESTS":
    case "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE":
    case "RATE_LIMITED":
      return "Za dużo prób. Odczekaj chwilę i spróbuj ponownie.";
    case "INVALID_CODE":
    case "INVALID_BACKUP_CODE":
      return "Kod jest nieprawidłowy lub został już użyty.";
    case "ACCOUNT_TEMPORARILY_LOCKED":
      return "Weryfikacja została chwilowo zablokowana. Spróbuj ponownie później.";
    default:
      return error?.status === 429
        ? "Za dużo prób. Odczekaj chwilę i spróbuj ponownie."
        : "Nie udało się wykonać operacji. Sprawdź połączenie i spróbuj ponownie.";
  }
}

export function isSafeReturnPath(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\")
  );
}
